import * as React from 'react';
import {
  Backdrop,
  Box,
  Button,
  Fade,
  Grid,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { isValid } from 'swedish-personal-identity-number-validator';
import { useTranslation } from 'react-i18next';
import { addNewPatient } from '../../store/actions/general';
import { exportButtonStyle } from '../PatientDetails/PatientDetails';
import { SignupType } from '../../lib/interfaces';
import FlashSnackbar from '../common/FlashSnackbar';

import type { AppDispatch } from '../../store';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  p: 4,
  borderRadius: '5px',
};

export interface SimpleDialogProps {
  onClose?: () => void;
  modalOpen: boolean;
}

const AddPatient = ({ onClose = () => {}, modalOpen }: SimpleDialogProps) => {
  const [modalOpened, setModalOpened] = React.useState(false);

  const [flashOpen, setFlashOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [errorFlashOpen, setErrorFlashOpen] = useState(false);

  const { t } = useTranslation();

  const emptyForm = useMemo(
    () => ({ personalNumber: '', signupType: '' as SignupType | '' }),
    []
  );

  const emptyErrorsObject = useMemo(
    () => ({ personalNumber: false, signupType: false }),
    []
  );

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState(emptyErrorsObject);

  const getValidatedFieldHelperText = (field: 'personalNumber') => {
    if (!errors[field]) return undefined;
    if (formData[field] === '') return `${t(field)} ${t('isRequired')}`;
    return `${t(field)} ${t('invalidFormat')}`;
  };

  useEffect(() => {
    setModalOpened(modalOpen);
  }, [modalOpen]);

  const resetForm = () => {
    setFormData(emptyForm);
    setErrors(emptyErrorsObject);
  };

  const handleModalClose = () => {
    setModalOpened(false);
    onClose();
    resetForm();
  };

  const validateField = (field: keyof typeof formData, value: string) => {
    if (field === 'personalNumber') {
      return value === '' || !isValid(value);
    }
    return value === '';
  };

  const validateForm = () => {
    const newErrors = {
      personalNumber: validateField('personalNumber', formData.personalNumber),
      signupType: validateField('signupType', formData.signupType),
    };
    setErrors(newErrors);
    return newErrors;
  };

  const handleChange =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    };

  const handleSubmit = async () => {
    const anyErrors = validateForm();
    if (!Object.values(anyErrors).some((error) => error)) {
      try {
        await dispatch(
          addNewPatient({
            personalNumber: formData.personalNumber,
            signupType: formData.signupType as SignupType,
            isPatient: true,
            isAdmin: false,
          })
        );

        setModalOpened(false);
        setFlashOpen(true);
        resetForm();
        onClose();
      } catch {
        setErrorFlashOpen(true);
      }
    }
  };

  return (
    <>
      <Modal
        open={modalOpened}
        onClose={handleModalClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={modalOpened}>
          <Box sx={modalStyle}>
            <Typography style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {t('addNewPatient')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={t('personalNumber')}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={formData.personalNumber}
                  onChange={handleChange('personalNumber')}
                  error={errors.personalNumber}
                  helperText={getValidatedFieldHelperText('personalNumber')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('referralType')}
                  variant="outlined"
                  fullWidth
                  select
                  margin="normal"
                  value={formData.signupType}
                  onChange={handleChange('signupType')}
                  error={errors.signupType}
                  helperText={
                    errors.signupType &&
                    `${t('referralType')} ${t('isRequired')}`
                  }
                >
                  {(
                    [
                      'referralPrimaryCare',
                      'referralSpecialistCare',
                    ] as SignupType[]
                  ).map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`signupType.${type}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Button
              onClick={handleSubmit}
              variant="contained"
              style={{
                ...exportButtonStyle,
                float: 'right',
                marginTop: '20px',
              }}
              sx={{ textTransform: 'none' }}
            >
              {t('create')}
            </Button>
          </Box>
        </Fade>
      </Modal>
      <FlashSnackbar
        open={flashOpen}
        msg={t('patientAddedSuccessfully')}
        severity="success"
        onClose={() => setFlashOpen(false)}
      />
      <FlashSnackbar
        open={errorFlashOpen}
        msg={t('patientAddFailed')}
        severity="error"
        onClose={() => setErrorFlashOpen(false)}
      />
    </>
  );
};

export default AddPatient;
